import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { QueryOptions, useQuery } from '@globalconnect/query';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { LoadingStatesService } from '../../report-inputs/service/loading-states.service';
import { RolloutProgressReportEntities } from '../entities';
import {
  rpInspectionReportDataNameKey,
  rpReportGroupByNameKey,
  rpReportGroupsKey,
} from '../entities/query-keys';
import { InspectionReportInitService } from '../initial-values/inspection-report-init.service';
import { RolloutProgressReportGroupsApiService } from './api/rollout-progress-report-groups-api.service';

@Injectable({
  providedIn: 'root',
})
export class RolloutProgressReportGroupsService {
  private storedReportGroups$ = new BehaviorSubject<
    Record<string, RolloutProgressReportEntities.v3.ReportGroupsResponse>
  >({});
  private readonly _storedInspectionReportGroups = signal(
    {} as Record<
      string,
      RolloutProgressReportEntities.v3.InspectionReportGroupsData
    >,
  );
  readonly storedInspectionReportGroups =
    this._storedInspectionReportGroups.asReadonly();

  isSiteAndHubReport = this.initService.isSiteAndHubReport;

  constructor(
    private apiService: RolloutProgressReportGroupsApiService,
    private readonly loadingStatesService: LoadingStatesService,
    private readonly initService: InspectionReportInitService,
  ) {}

  //v2
  setInitialStoredInspectionReportGroups = (
    reportId: string,
    inspectionReportGroups: RolloutProgressReportEntities.v3.InspectionReportGroupsData,
  ): void => {
    this._storedInspectionReportGroups.set({
      [reportId]: inspectionReportGroups,
    });
  };

  addAnswerToStoredInspectionReportGroups = (
    reportId: string,
    updatedAnswerWithGroupStatus: RolloutProgressReportEntities.v3.AnswerWithGroupStatus,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
  ): void => {
    const updatedStoredInspectionReportGroups =
      this.updateStoredInspectionReportGroups(reportId, (inspectionGroup) =>
        this.updateInspectionGroup(
          inspectionGroup,
          null,
          updatedAnswerWithGroupStatus,
          reportGroup,
          (selfInspection) =>
            this.updateSelfInspection(
              selfInspection,
              updatedAnswerWithGroupStatus.answer,
              updatedAnswerWithGroupStatus.answer.selfInspectionDefinitionId,
            ),
        ),
      );

    this._storedInspectionReportGroups.set(updatedStoredInspectionReportGroups);
    this.loadingStatesService.setIsLoadingValue(
      updatedAnswerWithGroupStatus.answer.selfInspectionDefinitionId,
      false,
    );
  };

  removeAnswerFromStoredInspectionReporGroups = (
    reportId: string,
    updatedGroupResponse: RolloutProgressReportEntities.v3.GroupObjectResponse,
    updatedAnswer: RolloutProgressReportEntities.v3.AnswerResponse,
    definitionId: string,
  ): void => {
    const updatedStoredInspectionReportGroups =
      this.updateStoredInspectionReportGroups(reportId, (inspectionGroup) =>
        this.updateInspectionGroup(
          inspectionGroup,
          updatedGroupResponse,
          null,
          updatedGroupResponse.groupInfo.groupName,
          (selfInspection) =>
            this.updateSelfInspection(
              selfInspection,
              Object.keys(updatedAnswer).length ? updatedAnswer : null,
              definitionId,
            ),
        ),
      );
    this._storedInspectionReportGroups.set(updatedStoredInspectionReportGroups);
  };

  private readonly updateStoredInspectionReportGroups = (
    reportId: string,
    updateFn: (
      inspectionGroup: RolloutProgressReportEntities.v3.InspectionReportGroup,
    ) => RolloutProgressReportEntities.v3.InspectionReportGroup,
  ): Record<
    string,
    RolloutProgressReportEntities.v3.InspectionReportGroupsData
  > => {
    return {
      ...this._storedInspectionReportGroups(),
      [reportId]: {
        ...this._storedInspectionReportGroups()[reportId],
        items:
          this._storedInspectionReportGroups()[reportId].items.map(updateFn),
      },
    };
  };

  private readonly updateInspectionGroup = (
    inspectionGroup: RolloutProgressReportEntities.v3.InspectionReportGroup,
    updatedGroupResponse: RolloutProgressReportEntities.v3.GroupObjectResponse | null,
    updatedAnswerWithGroupStatus: RolloutProgressReportEntities.v3.AnswerWithGroupStatus | null,
    groupName: string,
    updateSelfInspectionFn: (
      selfInspection: RolloutProgressReportEntities.v3.CombinedSelfInspections,
    ) => RolloutProgressReportEntities.v3.CombinedSelfInspections,
  ): RolloutProgressReportEntities.v3.InspectionReportGroup => {
    if (inspectionGroup.group.groupInfo.groupName === groupName) {
      return {
        group: updatedAnswerWithGroupStatus
          ? {
              ...(updatedGroupResponse ?? inspectionGroup.group),
              groupStatus: updatedAnswerWithGroupStatus.groupStatus,
            }
          : (updatedGroupResponse ?? inspectionGroup.group),
        selfInspections: inspectionGroup.selfInspections.map(
          updateSelfInspectionFn,
        ),
      };
    }
    return inspectionGroup;
  };

  private readonly updateSelfInspection = (
    selfInspection: RolloutProgressReportEntities.v3.CombinedSelfInspections,
    answer: RolloutProgressReportEntities.v3.AnswerResponse | null,
    selfInspectionDefinitionId: string,
  ): RolloutProgressReportEntities.v3.CombinedSelfInspections => {
    if (selfInspection.definition.id === selfInspectionDefinitionId) {
      return {
        definition: selfInspection.definition,
        answer,
      };
    }
    return selfInspection;
  };

  getReportGroups$(
    reportId: string,
    options?: Partial<
      QueryOptions<
        RolloutProgressReportEntities.v3.ReportGroupsResponse,
        HttpErrorResponse,
        never,
        RolloutProgressReportEntities.v3.ReportGroupsResponse
      >
    >,
  ) {
    return useQuery(
      [rpReportGroupsKey, reportId],
      () => this.apiService.getReportGroups(reportId),
      {
        onSuccess: (data) => this.setInitialStoredReportGroups(reportId, data),
        onError: (err) => console.error(err),
        cacheTime: 0,
        staleTime: 0,
        ...options,
      },
    ).getState$();
  }

  getReportGroupByName$ = (
    reportId: string,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
    definitionId: string,
    updatedAnswerResponse: RolloutProgressReportEntities.v3.AnswerResponse,
    options?: Partial<
      QueryOptions<
        RolloutProgressReportEntities.v3.GroupObjectResponse,
        HttpErrorResponse,
        never,
        RolloutProgressReportEntities.v3.GroupObjectResponse
      >
    >,
  ) =>
    useQuery(
      [rpReportGroupByNameKey, reportId + reportGroup],
      () => this.apiService.getReportGroupByName(reportId, reportGroup),
      {
        onSuccess: (updatedGroupResponse) => {
          this.isSiteAndHubReport()
            ? this.setStoredReportGroupByName(
                reportId,
                reportGroup,
                updatedGroupResponse,
                updatedAnswerResponse,
                definitionId,
              )
            : this.removeAnswerFromStoredInspectionReporGroups(
                reportId,
                updatedGroupResponse,
                updatedAnswerResponse,
                definitionId,
              );
          this.updateLoadingState(
            Object.keys(updatedAnswerResponse).length
              ? updatedAnswerResponse
              : definitionId,
          );
        },
        onError: () => {
          this.updateLoadingState(
            Object.keys(updatedAnswerResponse).length
              ? updatedAnswerResponse
              : definitionId,
          );
        },
        cacheTime: 0,
        staleTime: 0,
        ...options,
      },
    ).getState$();

  getInspectionReportGroupsData$ = (
    reportId: string,
    typeId: RolloutProgressReportEntities.v3.MarkerTypes,
    options?: Partial<
      QueryOptions<
        RolloutProgressReportEntities.v3.InspectionReportGroupsData,
        HttpErrorResponse,
        never,
        RolloutProgressReportEntities.v3.InspectionReportGroupsData
      >
    >,
  ): Observable<RolloutProgressReportEntities.v3.InspectionReportGroupsDataQueryLoadingState> =>
    useQuery(
      [rpInspectionReportDataNameKey, reportId],
      () => this.apiService.getInspectionReportGroupsData(reportId, typeId),
      {
        onSuccess: (inspectionReports) => {
          this.setInitialStoredInspectionReportGroups(
            reportId,
            inspectionReports,
          );
        },
        cacheTime: 0,
        staleTime: 0,
        ...options,
      },
    ).getState$();

  getStoredReportGroupByReportId$ = (
    reportId: string,
  ): Observable<RolloutProgressReportEntities.v3.ReportGroupsResponse> =>
    this.storedReportGroups$.pipe(
      map((reportGroups) => reportGroups[reportId]),
    );

  setInitialStoredReportGroups = (
    reportId: string,
    reportGroups: RolloutProgressReportEntities.v3.ReportGroupsResponse,
  ): void => {
    this.storedReportGroups$.next({
      ...this.storedReportGroups$.getValue(),
      [reportId]: reportGroups,
    });
  };

  setStoredReportGroupByName = (
    reportId: string,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
    updatedReportGroup: RolloutProgressReportEntities.v3.GroupObjectResponse,
    updatedAnswer: RolloutProgressReportEntities.v3.AnswerResponse,
    definitionId: string,
  ): void => {
    !Object.keys(updatedAnswer).length
      ? this.removeStoredAnswer(
          reportId,
          reportGroup,
          updatedReportGroup,
          definitionId,
        )
      : this.storedReportGroups$.next({
          ...this.storedReportGroups$.getValue(),
          [reportId]: {
            ...this.storedReportGroups$.getValue()[reportId],
            items: this.storedReportGroups$
              .getValue()
              // eslint-disable-next-line no-unexpected-multiline
              [reportId].items.map((storedGroupWithAnswers) =>
                storedGroupWithAnswers.group.groupInfo.groupName ===
                  reportGroup && updatedAnswer
                  ? this.updateStoredReportGroup(
                      storedGroupWithAnswers,
                      updatedReportGroup,
                      updatedAnswer,
                    )
                  : storedGroupWithAnswers,
              ),
          },
        });
  };

  updateStoredReportGroup = (
    storedGroupWithAnswers: RolloutProgressReportEntities.v3.AnswersByGroupItem,
    updatedReportGroup: RolloutProgressReportEntities.v3.GroupObjectResponse,
    newAnswer: RolloutProgressReportEntities.v3.AnswerResponse,
  ): RolloutProgressReportEntities.v3.AnswersByGroupItem => {
    const doesExist = storedGroupWithAnswers.selfInspections.some(
      (answer) =>
        answer.selfInspectionDefinitionId ===
        newAnswer.selfInspectionDefinitionId,
    );

    return doesExist
      ? this.updateExistingAnswerInReportGroup(
          storedGroupWithAnswers,
          updatedReportGroup,
          newAnswer,
        )
      : this.addNewAnswerToStoredReportGroup(
          storedGroupWithAnswers,
          updatedReportGroup,
          newAnswer,
        );
  };

  updateExistingAnswerInReportGroup = (
    storedGroupWithAnswers: RolloutProgressReportEntities.v3.AnswersByGroupItem,
    updatedReportGroup: RolloutProgressReportEntities.v3.GroupObjectResponse,
    newAnswer: RolloutProgressReportEntities.v3.AnswerResponse,
  ) => ({
    group: updatedReportGroup,
    selfInspections: storedGroupWithAnswers.selfInspections.length
      ? [
          ...storedGroupWithAnswers.selfInspections.map((storedAnswer) =>
            storedAnswer.selfInspectionDefinitionId ===
            newAnswer.selfInspectionDefinitionId
              ? newAnswer
              : storedAnswer,
          ),
        ]
      : [newAnswer],
  });

  addNewAnswerToStoredReportGroup = (
    storedGroupWithAnswers: RolloutProgressReportEntities.v3.AnswersByGroupItem,
    updatedReportGroup: RolloutProgressReportEntities.v3.GroupObjectResponse,
    newAnswer: RolloutProgressReportEntities.v3.AnswerResponse,
  ) => ({
    group: updatedReportGroup,
    selfInspections: [...storedGroupWithAnswers.selfInspections, newAnswer],
  });

  removeStoredAnswer = (
    reportId: string,
    reportGroup: RolloutProgressReportEntities.v3.Groups,
    updatedReportGroup: RolloutProgressReportEntities.v3.GroupObjectResponse,
    definitionId: string,
  ): void => {
    this.storedReportGroups$.next({
      ...this.storedReportGroups$.getValue(),
      [reportId]: {
        currentCount:
          this.storedReportGroups$.getValue()[reportId].currentCount,
        items: this.storedReportGroups$
          .getValue()
          // eslint-disable-next-line no-unexpected-multiline
          [reportId].items.map((item) =>
            item.group.groupInfo.groupName === reportGroup
              ? {
                  group: updatedReportGroup,
                  selfInspections: item.selfInspections.filter(
                    (selfInspection) =>
                      selfInspection.selfInspectionDefinitionId !==
                      definitionId,
                  ),
                }
              : item,
          ),
      },
    });
  };

  updateLoadingState(
    updatedAnswerResponse:
      | RolloutProgressReportEntities.v3.AnswerResponse
      | string,
  ) {
    this.loadingStatesService.setIsLoadingValue(
      typeof updatedAnswerResponse === 'string'
        ? updatedAnswerResponse
        : updatedAnswerResponse.selfInspectionDefinitionId,
      false,
    );
  }
}
